import { SERVER_URL, STORAGE_BASE } from '../libs/server'

export interface DefaultSetupEntry {
  expression: string
  enable: boolean
}

export interface OutfitVariant {
  id: string
  name: string
  vrm?: string
  live2d?: string
  previewUrl?: string
  price?: number
  outfit_top?: string
  outfit_bottom?: string
  defaultSetup?: DefaultSetupEntry[]
}

export interface AccessoryVariant {
  id: string
  name: string
  previewUrl?: string
  price?: number
  expression: string
}

export interface CharacterCardAvatarModel {
  type: 'vrm' | 'live2d'
  url: string
  previewUrl?: string
}

export interface CharacterCardRemote {
  id: string
  name: string
  description: string
  creator: string
  /** 卡片资源版本号，更新模型/图片后递增，服务器会给资源 URL 追加 ?v=<version> */
  version?: number
  /** 模型类型：'Live2D' 或 'VRM'，前端用来判断加载方式 */
  modelType?: string
  /** 角色性别目录名（female/male），R2 characters.json 用于拼接资源路径 */
  gender?: string
  tags: string[]
  personality: string
  scenario: string
  systemPrompt: string
  greetings: string[]
  coverImage?: string | null
  appearance?: {
    age?: string
    body?: string
    height?: string
    weight?: string
    hair?: string
    eyes?: string
    outfit_top?: string
    outfit_bottom?: string
  }
  outfits?: OutfitVariant[]
  accessories?: AccessoryVariant[]
  hitInteractions?: Record<string, { expression?: string, motion?: string }>
  // avatarModel 已废弃，保留向后兼容
  avatarModel?: CharacterCardAvatarModel | null
}

const API_BASE = `${SERVER_URL}/api/v1/character-cards`

function resolveUrl(url: string | undefined, card: CharacterCardRemote): string | undefined {
  if (!url)
    return undefined
  if (url.startsWith('http://') || url.startsWith('https://'))
    return url
  // VPS API 路径（后端已 resolve）— 重定向到 R2 或回退 VPS
  if (url.startsWith('/api/v1/character-cards/')) {
    if (STORAGE_BASE) {
      const m = url.match(/^\/api\/v1\/character-cards\/(.+)$/)
      if (m)
        return `${STORAGE_BASE}/characters/${m[1]}`
    }
    return `${SERVER_URL}${url}`
  }
  // 原始相对路径（R2 characters.json 中的 model.zip / preview.png 等）
  const gender = card.gender
  const version = card.version
  if (STORAGE_BASE && gender) {
    const full = `${STORAGE_BASE}/characters/${gender}/${card.id}/${url}`
    return version ? `${full}?v=${version}` : full
  }
  // 回退 VPS
  if (gender) {
    const apiPath = `/api/v1/character-cards/${gender}/${card.id}/${url}`
    return version ? `${SERVER_URL}${apiPath}?v=${version}` : `${SERVER_URL}${apiPath}`
  }
  return `${SERVER_URL}${url.startsWith('/') ? '' : '/'}${url}`
}

function resolveCardUrls(card: CharacterCardRemote): CharacterCardRemote {
  const resolved = { ...card }

  // 解析 coverImage
  if (resolved.coverImage) {
    resolved.coverImage = resolveUrl(resolved.coverImage, resolved) ?? null
  }

  // 解析 outfits 中的 URL
  if (resolved.outfits) {
    resolved.outfits = resolved.outfits.map(outfit => ({
      ...outfit,
      vrm: resolveUrl(outfit.vrm, resolved),
      live2d: resolveUrl(outfit.live2d, resolved),
      previewUrl: resolveUrl(outfit.previewUrl, resolved),
    }))
  }

  // 解析 accessories 中的 URL
  if (resolved.accessories) {
    resolved.accessories = resolved.accessories.map(accessory => ({
      ...accessory,
      previewUrl: resolveUrl(accessory.previewUrl, resolved),
    }))
  }

  // 向后兼容：解析 avatarModel
  if (resolved.avatarModel) {
    resolved.avatarModel = {
      ...resolved.avatarModel,
      url: resolveUrl(resolved.avatarModel.url, resolved) ?? resolved.avatarModel.url,
      previewUrl: resolveUrl(resolved.avatarModel.previewUrl, resolved),
    }
  }
  // 如果没有 avatarModel，从 outfits 推断
  else if (resolved.outfits && resolved.outfits.length > 0) {
    const defaultOutfit = resolved.outfits.find(o => o.id === 'default') || resolved.outfits[0]
    const modelUrl = defaultOutfit.live2d || defaultOutfit.vrm
    const type = defaultOutfit.live2d ? 'live2d' : 'vrm'
    resolved.avatarModel = modelUrl
      ? {
          type,
          url: modelUrl,
          previewUrl: defaultOutfit.previewUrl,
        }
      : null
  }

  return resolved
}

/**
 * 获取默认服装（id=default）
 */
export function getDefaultOutfit(card: CharacterCardRemote): OutfitVariant | undefined {
  return card.outfits?.find(o => o.id === 'default')
}

/**
 * 获取默认服装的模型 URL
 * 支持 live2d 和 vrm 两种格式
 * 优先从 outfits 中获取，否则向后兼容使用 avatarModel
 */
export function getDefaultVrmUrl(card: CharacterCardRemote): string | undefined {
  const defaultOutfit = getDefaultOutfit(card)
  if (defaultOutfit) {
    // 优先返回 live2d，其次 vrm
    return defaultOutfit.live2d || defaultOutfit.vrm
  }
  // 向后兼容
  return card.avatarModel?.url
}

/**
 * 获取默认服装的预览图 URL
 * 优先从 outfits 中获取，否则向后兼容使用 avatarModel
 */
export function getDefaultPreviewUrl(card: CharacterCardRemote): string | undefined {
  const defaultOutfit = getDefaultOutfit(card)
  if (defaultOutfit) {
    return defaultOutfit.previewUrl
  }
  // 向后兼容
  return card.avatarModel?.previewUrl
}

// NOTICE: 同会话内缓存 card.json，避免切换 outfit/accessory 时重复请求。
// 刷新页面时缓存自动清空（内存），重新拉取最新 version。
const cardCache = new Map<string, Promise<CharacterCardRemote>>()

export async function fetchCharacterCards(gender?: 'female' | 'male'): Promise<CharacterCardRemote[]> {
  // R2 直连：从 characters.json 获取全部卡片，客户端过滤 gender
  const url = STORAGE_BASE
    ? `${STORAGE_BASE}/characters/characters.json`
    : gender
      ? `${API_BASE}?gender=${gender}`
      : API_BASE
  const res = await fetch(url, { cache: 'no-cache' })
  if (!res.ok)
    throw new Error('Failed to fetch character cards')

  let cards = await res.json() as CharacterCardRemote[]
  if (gender && STORAGE_BASE)
    cards = cards.filter(c => c.gender === gender)

  const resolved = cards.map(resolveCardUrls)
  // 批量请求时顺带填充单卡缓存
  for (const card of resolved) {
    cardCache.set(card.id, Promise.resolve(card))
  }
  return resolved
}

export async function fetchCharacterCard(id: string): Promise<CharacterCardRemote> {
  const cached = cardCache.get(id)
  if (cached)
    return cached

  const promise = (async () => {
    // R2 直连：拉取 characters.json 后按 id 查找
    if (STORAGE_BASE) {
      const res = await fetch(`${STORAGE_BASE}/characters/characters.json`, { cache: 'no-cache' })
      if (!res.ok)
        throw new Error(`Failed to fetch character card "${id}"`)
      const cards = await res.json() as CharacterCardRemote[]
      const card = cards.find(c => c.id === id)
      if (!card)
        throw new Error(`Character card "${id}" not found`)
      // 顺带缓存全部卡片
      const resolved = cards.map(resolveCardUrls)
      for (const c of resolved)
        cardCache.set(c.id, Promise.resolve(c))
      return resolved.find(c => c.id === id)!
    }
    // VPS 回退
    const res = await fetch(`${API_BASE}/${id}`, { cache: 'no-cache' })
    if (!res.ok)
      throw new Error(`Failed to fetch character card "${id}"`)
    const card = await res.json() as CharacterCardRemote
    return resolveCardUrls(card)
  })()

  cardCache.set(id, promise)
  return promise
}
